import { LineChartDataPoint, YAxisRange, XAxisLabelStrategy } from "./types";

// ===== MONTHLY SYSTEM =====

/**
 * Get month name from date string in timezone-safe way
 * Uses UTC methods to avoid timezone shifting - "2024-08-01" should ALWAYS be August
 */
export const getMonthFromDateString = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-US", {
    month: "short",
    timeZone: "UTC",
  });
};

/**
 * Check if this date represents the first occurrence of a month in the dataset (original system)
 */
export const isMonthBoundary = (
  dateStr: string,
  allData: TransformedChartData[],
): boolean => {
  const currentMonth = getMonthFromDateString(dateStr);
  if (!currentMonth) return false;

  const currentIndex = allData.findIndex((item) => item.period === dateStr);
  if (currentIndex === -1) return false;

  for (let i = 0; i < currentIndex; i++) {
    const prevMonth = getMonthFromDateString(allData[i].period);
    if (prevMonth === currentMonth) {
      return false;
    }
  }

  return true;
};

// ===== PROGRESSIVE SYSTEM =====

/**
 * Get date components in a timezone-safe way
 */
export const getDateComponents = (dateStr: string) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;

  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
    date: date.getUTCDate(),
    dayOfWeek: date.getUTCDay(),
  };
};

/**
 * Check if a date is a "nice" date (1st, 15th of month, or Monday)
 */
export const isNiceDate = (dateStr: string): boolean => {
  const components = getDateComponents(dateStr);
  if (!components) return false;

  const { date, dayOfWeek } = components;

  return date === 1 || date === 15 || dayOfWeek === 1;
};

/**
 * Get the next/previous nice date with context awareness
 */
export const getNiceDate = (
  dateStr: string,
  direction: "next" | "prev",
  context: "weekly" | "bimonthly" | "monthly" | "quarterly" = "monthly",
): string => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  const components = getDateComponents(dateStr);
  if (!components) return dateStr;

  if (context === "quarterly") {
    const quarterStarts = [0, 3, 6, 9];

    if (direction === "next") {
      const nextQuarterMonth = quarterStarts.find(
        (month) => month > components.month,
      );
      if (nextQuarterMonth !== undefined) {
        const nextQuarter = new Date(date);
        nextQuarter.setUTCMonth(nextQuarterMonth, 1);
        return nextQuarter.toISOString().split("T")[0];
      } else {
        const nextYear = new Date(date);
        nextYear.setUTCFullYear(date.getUTCFullYear() + 1, 0, 1);
        return nextYear.toISOString().split("T")[0];
      }
    } else {
      const currentQuarterMonth = quarterStarts
        .slice()
        .reverse()
        .find((month) => month <= components.month);
      if (currentQuarterMonth !== undefined) {
        const currentQuarter = new Date(date);
        currentQuarter.setUTCMonth(currentQuarterMonth, 1);
        return currentQuarter.toISOString().split("T")[0];
      } else {
        const prevYear = new Date(date);
        prevYear.setUTCFullYear(date.getUTCFullYear() - 1, 9, 1);
        return prevYear.toISOString().split("T")[0];
      }
    }
  }

  if (context === "bimonthly") {
    if (direction === "next") {
      if (components.date < 15) {
        const fifteenth = new Date(date);
        fifteenth.setUTCDate(15);
        return fifteenth.toISOString().split("T")[0];
      }
      const firstOfNextMonth = new Date(date);
      firstOfNextMonth.setUTCMonth(date.getUTCMonth() + 1, 1);
      return firstOfNextMonth.toISOString().split("T")[0];
    } else {
      if (components.date > 15) {
        const fifteenth = new Date(date);
        fifteenth.setUTCDate(15);
        return fifteenth.toISOString().split("T")[0];
      }
      if (components.date > 1) {
        const firstOfMonth = new Date(date);
        firstOfMonth.setUTCDate(1);
        return firstOfMonth.toISOString().split("T")[0];
      }
      const fifteenthOfPrevMonth = new Date(date);
      fifteenthOfPrevMonth.setUTCMonth(date.getUTCMonth() - 1, 15);
      return fifteenthOfPrevMonth.toISOString().split("T")[0];
    }
  }

  if (direction === "next") {
    const daysUntilMonday = (8 - date.getUTCDay()) % 7;
    if (daysUntilMonday > 0 && context === "weekly") {
      const nextMonday = new Date(date);
      nextMonday.setUTCDate(date.getUTCDate() + daysUntilMonday);
      return nextMonday.toISOString().split("T")[0];
    }

    if (context === "monthly") {
      const firstOfNextMonth = new Date(date);
      firstOfNextMonth.setUTCMonth(date.getUTCMonth() + 1, 1);
      return firstOfNextMonth.toISOString().split("T")[0];
    }

    if (components.date < 15) {
      const fifteenth = new Date(date);
      fifteenth.setUTCDate(15);
      return fifteenth.toISOString().split("T")[0];
    }

    const firstOfNextMonth = new Date(date);
    firstOfNextMonth.setUTCMonth(date.getUTCMonth() + 1, 1);
    return firstOfNextMonth.toISOString().split("T")[0];
  } else {
    const daysSinceMonday = (date.getUTCDay() + 6) % 7;
    if (daysSinceMonday > 0 && context === "weekly") {
      const prevMonday = new Date(date);
      prevMonday.setUTCDate(date.getUTCDate() - daysSinceMonday);
      return prevMonday.toISOString().split("T")[0];
    }

    if (context === "monthly") {
      const firstOfMonth = new Date(date);
      firstOfMonth.setUTCDate(1);
      return firstOfMonth.toISOString().split("T")[0];
    }

    if (components.date > 15) {
      const fifteenth = new Date(date);
      fifteenth.setUTCDate(15);
      return fifteenth.toISOString().split("T")[0];
    }

    if (components.date > 1) {
      const firstOfMonth = new Date(date);
      firstOfMonth.setUTCDate(1);
      return firstOfMonth.toISOString().split("T")[0];
    }

    const fifteenthOfPrevMonth = new Date(date);
    fifteenthOfPrevMonth.setUTCMonth(date.getUTCMonth() - 1, 15);
    return fifteenthOfPrevMonth.toISOString().split("T")[0];
  }
};

/**
 * Generate all dates between two dates that match the criteria
 */
const generateDateRange = (
  startDate: string,
  endDate: string,
  criteria: (date: string) => boolean,
): string[] => {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  const current = new Date(start);

  while (current <= end) {
    const dateStr = current.toISOString().split("T")[0];
    if (criteria(dateStr)) {
      dates.push(dateStr);
    }
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return dates;
};

/**
 * Generate progressive X-axis labels based on data density
 */
export const generateProgressiveLabels = (dates: string[]): string[] => {
  if (dates.length === 0) return [];

  const sortedDates = [...dates].sort();
  const firstDate = sortedDates[0];
  const lastDate = sortedDates[sortedDates.length - 1];

  if (sortedDates.length === 1) {
    const singleDate = sortedDates[0];
    const date = new Date(singleDate);

    const prevDate = new Date(date);
    prevDate.setUTCDate(date.getUTCDate() - 3);
    const prevNiceDate = prevDate.toISOString().split("T")[0];

    const nextDate = new Date(date);
    nextDate.setUTCDate(date.getUTCDate() + 3);
    const nextNiceDate = nextDate.toISOString().split("T")[0];

    const labels = [prevNiceDate, singleDate, nextNiceDate].sort();
    return labels;
  }

  const timeSpanMs =
    new Date(lastDate).getTime() - new Date(firstDate).getTime();
  const timeSpanDays = timeSpanMs / (1000 * 60 * 60 * 24);

  const niceFirstDate =
    timeSpanDays > 365
      ? getNiceDate(firstDate, "prev", "quarterly")
      : timeSpanDays > 90
        ? getNiceDate(firstDate, "prev", "monthly")
        : getNiceDate(firstDate, "prev", "bimonthly");
  const niceLastDate =
    timeSpanDays > 365
      ? getNiceDate(lastDate, "next", "quarterly")
      : timeSpanDays > 90
        ? getNiceDate(lastDate, "next", "monthly")
        : getNiceDate(lastDate, "next", "bimonthly");

  let labeledDates: string[] = [];

  if (timeSpanDays <= 30) {
    labeledDates = generateDateRange(niceFirstDate, niceLastDate, (date) => {
      const components = getDateComponents(date);
      return components
        ? components.date === 1 || components.date === 15
        : false;
    });

    if (!labeledDates.includes(niceFirstDate)) {
      labeledDates.unshift(niceFirstDate);
    }
    if (!labeledDates.includes(niceLastDate)) {
      labeledDates.push(niceLastDate);
    }
  } else if (timeSpanDays <= 90) {
    labeledDates = generateDateRange(niceFirstDate, niceLastDate, (date) => {
      const components = getDateComponents(date);
      return components
        ? components.date === 1 || components.date === 15
        : false;
    });

    if (!labeledDates.includes(niceFirstDate)) {
      labeledDates.unshift(niceFirstDate);
    }
    if (!labeledDates.includes(niceLastDate)) {
      labeledDates.push(niceLastDate);
    }
  } else if (timeSpanDays <= 365) {
    labeledDates = generateDateRange(niceFirstDate, niceLastDate, (date) => {
      const components = getDateComponents(date);
      return components ? components.date === 1 : false;
    });

    if (!labeledDates.includes(niceFirstDate)) {
      labeledDates.unshift(niceFirstDate);
    }
    if (!labeledDates.includes(niceLastDate)) {
      labeledDates.push(niceLastDate);
    }
  } else {
    labeledDates = generateDateRange(niceFirstDate, niceLastDate, (date) => {
      const components = getDateComponents(date);
      return components
        ? components.date === 1 && [0, 3, 6, 9].includes(components.month)
        : false;
    });

    if (!labeledDates.includes(niceFirstDate)) {
      labeledDates.unshift(niceFirstDate);
    }
    if (!labeledDates.includes(niceLastDate)) {
      labeledDates.push(niceLastDate);
    }
  }

  return [...new Set(labeledDates)].sort();
};

/**
 * Format date for X-axis display (progressive system)
 */
export const formatXAxisDateProgressive = (
  dateStr: string,
  allDates: string[],
): string => {
  const components = getDateComponents(dateStr);
  if (!components) return dateStr;

  const timeSpanMs =
    new Date(allDates[allDates.length - 1]).getTime() -
    new Date(allDates[0]).getTime();
  const timeSpanDays = timeSpanMs / (1000 * 60 * 60 * 24);

  if (timeSpanDays > 365) {
    return getMonthFromDateString(dateStr);
  }

  if (timeSpanDays > 90) {
    return getMonthFromDateString(dateStr);
  }

  if (timeSpanDays > 30) {
    const monthName = getMonthFromDateString(dateStr);
    return `${monthName} ${components.date}`;
  }

  const monthName = getMonthFromDateString(dateStr);
  return `${monthName} ${components.date}`;
};

const progressiveLabelsCache = new Map<string, string[]>();

/**
 * Generate a unique key for caching progressive labels based on original data
 */
const generateCacheKey = (originalDataDates: string[]): string => {
  return originalDataDates.sort().join(",");
};

/**
 * Check if this date should show a label based on progressive labeling
 */
export const shouldShowLabelProgressive = (
  dateStr: string,
  allData: TransformedChartData[],
): boolean => {
  const originalDataDates = allData
    .filter((item) => item.originalIndex >= 0)
    .map((item) => item.period)
    .sort();

  const cacheKey = generateCacheKey(originalDataDates);
  const progressiveLabels = progressiveLabelsCache.get(cacheKey) || [];

  const shouldShow = progressiveLabels.includes(dateStr);

  return shouldShow;
};

// ===== SHARED UTILITIES =====

/**
 * Utility function for calculating Y-axis domain with optional custom range
 */
export const calculateYAxisDomain = (
  values: number[],
  customRange?: YAxisRange,
  useProgressiveSystem = false,
): [number, number] => {
  if (values.length === 0) {
    return useProgressiveSystem ? [0, 100] : [0, 1];
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  if (useProgressiveSystem) {
    let finalMin = 0;
    let finalMax = 100;

    if (customRange?.min !== undefined) {
      finalMin = customRange.min;
    }

    if (customRange?.max !== undefined) {
      finalMax = customRange.max;
    }

    if (finalMax <= finalMin) {
      finalMax = finalMin + 10;
    }

    return [finalMin, finalMax];
  } else {
    const range = maxValue - minValue;
    const padding = range * 0.1;

    const defaultMin = Math.max(0, minValue - padding);
    const defaultMax = maxValue + padding;

    let finalMin = defaultMin;
    let finalMax = defaultMax;

    if (customRange?.min !== undefined) {
      finalMin = customRange.min;
    }

    if (customRange?.max !== undefined) {
      finalMax = customRange.max;
    }

    if (finalMax <= finalMin) {
      finalMax = finalMin + (range > 0 ? range * 0.1 : 1);
    }

    return [Math.floor(finalMin * 10) / 10, Math.ceil(finalMax * 10) / 10];
  }
};

export interface TransformedChartData {
  period: string;
  current: number | null;
  comparison: number | null | undefined;
  isPartial: boolean;
  comparisonIsPartial: boolean;
  originalIndex: number;
}

export interface ChartDataProcessingResult {
  transformedData: TransformedChartData[];
  yAxisDomain: [number, number];
  hasPartialData: boolean;
}

/**
 * Transforms and merges current and comparison data for chart rendering
 * Supports both original and progressive systems
 */
export const processChartData = (
  data: LineChartDataPoint[],
  comparisonData?: LineChartDataPoint[],
  interval?: string,
  customYAxisRange?: YAxisRange,
  xAxisLabelStrategy: XAxisLabelStrategy = "monthly",
): ChartDataProcessingResult => {
  const useProgressiveSystem = xAxisLabelStrategy === "progressive";
  const dataMap = new Map<string, TransformedChartData>();

  data.forEach((point, index) => {
    const period =
      typeof point.date === "string" ? point.date : point.date.toString();

    let convertedValue = point.value;

    if (
      useProgressiveSystem &&
      point.value !== null &&
      point.value !== undefined
    ) {
      convertedValue = Math.round(point.value);
    }

    dataMap.set(period, {
      period,
      current: convertedValue,
      comparison: comparisonData ? null : undefined,
      isPartial: point.isPartial || false,
      comparisonIsPartial: false,
      originalIndex: index,
    });
  });

  if (comparisonData) {
    comparisonData.forEach((point) => {
      const period =
        typeof point.date === "string" ? point.date : point.date.toString();
      const existing = dataMap.get(period);
      if (existing) {
        let convertedValue = point.value;

        if (
          useProgressiveSystem &&
          point.value !== null &&
          point.value !== undefined
        ) {
          convertedValue = Math.round(point.value);
        }

        existing.comparison = convertedValue;
        existing.comparisonIsPartial = point.isPartial || false;
      }
    });
  }

  if (useProgressiveSystem) {
    const originalDataDates = Array.from(dataMap.keys());
    const progressiveLabels = generateProgressiveLabels(originalDataDates);

    const cacheKey = generateCacheKey(originalDataDates);
    progressiveLabelsCache.set(cacheKey, progressiveLabels);

    const sortedOriginalDataDates = originalDataDates.sort();
    const firstDataDate = sortedOriginalDataDates[0];
    const lastDataDate =
      sortedOriginalDataDates[sortedOriginalDataDates.length - 1];

    const sortedProgressiveLabels = progressiveLabels.sort();
    const firstLabel = sortedProgressiveLabels[0];
    const lastLabel =
      sortedProgressiveLabels[sortedProgressiveLabels.length - 1];

    const startDate = new Date(firstLabel);
    const endDate = new Date(lastLabel);
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split("T")[0];

      if (!dataMap.has(dateStr)) {
        const labelDateTime = new Date(dateStr).getTime();
        const firstDataTime = new Date(firstDataDate).getTime();
        const lastDataTime = new Date(lastDataDate).getTime();

        const isBookend =
          labelDateTime < firstDataTime || labelDateTime > lastDataTime;
        const value = isBookend ? null : 0;

        dataMap.set(dateStr, {
          period: dateStr,
          current: value,
          comparison: comparisonData ? value : undefined,
          isPartial: false,
          comparisonIsPartial: false,
          originalIndex: -1,
        });
      }

      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }
  }

  const transformed = Array.from(dataMap.values()).sort((a, b) => {
    const dateA = new Date(a.period);
    const dateB = new Date(b.period);
    return dateA.getTime() - dateB.getTime();
  });

  const allValues: number[] = [];
  transformed.forEach((point) => {
    const shouldInclude = useProgressiveSystem
      ? point.originalIndex >= 0
      : true;

    if (shouldInclude) {
      if (
        point.current !== null &&
        point.current !== undefined &&
        isFinite(point.current) &&
        !isNaN(point.current)
      ) {
        allValues.push(point.current);
      }
      if (
        point.comparison !== null &&
        point.comparison !== undefined &&
        isFinite(point.comparison) &&
        !isNaN(point.comparison)
      ) {
        allValues.push(point.comparison);
      }
    }
  });

  const yDomain = calculateYAxisDomain(
    allValues,
    useProgressiveSystem
      ? { min: 0, max: 100, ...customYAxisRange }
      : customYAxisRange,
    useProgressiveSystem,
  );

  const hasPartial =
    data.length > 0 && data[data.length - 1]?.isPartial === true;

  return {
    transformedData: transformed,
    yAxisDomain: yDomain,
    hasPartialData: hasPartial,
  };
};
